/* import __COLOCATED_TEMPLATE__ from './greeting-prosemirror-composer.hbs'; */
/* RESPONSIBLE TEAM: team-messenger */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Store from '@ember-data/store';
import type IntlService from 'embercom/services/intl';
import createFragmentFromBlock from 'embercom/lib/blocks/create-fragment-from-block';
import { action } from '@ember/object';
import { BaseConfig, BlocksDocument, type ComposerPublicAPI } from '@intercom/composer-ember';
import AttributeInfoResolver from 'embercom/lib/common/template-attribute-resolver';
import { type TextDirection } from '@intercom/composer-ember/lib/config/composer-config';
import { type Block } from 'embercom/models/common/blocks/block';
import { htmlToTextContent } from 'embercom/lib/html-unescape';
import type IntercomAttribute from 'embercom/models/attribute';
import { next } from '@ember/runloop';

class ComposerConfig extends BaseConfig {
  autoFocus = true;
  textDirection: TextDirection = 'ltr';
  allowedBlocks = ['paragraph'];
  allowedInline = [];
  allowImplicitMarginParagraphs = true;
  allowTextAlignment = false;
  singleBlockMode = true;
  tools = [{ name: 'template-inserter' }, { name: 'fallback-editor' }];
  experimental = {
    hideInsertersOnMouseOut: true,
  };
  templating: { picker: string; resolver: AttributeInfoResolver };

  constructor({ resolver, placeholder }: { resolver: AttributeInfoResolver; placeholder: string }) {
    super();
    this.templating = { picker: 'common/attribute-picker', resolver };
    this.placeholder = placeholder;
  }
}

interface Args {
  locale: $TSFixMe;
  isMobilePlatform: boolean;
}

export default class GreetingProsemirrorComposer extends Component<Args> {
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare attributeService: $TSFixMe;
  @service declare intl: IntlService;

  @tracked blocksDoc: BlocksDocument;
  @tracked composerApi?: ComposerPublicAPI;

  constructor(owner: unknown, args: never) {
    super(owner, args);
    this.blocksDoc = new BlocksDocument(this.serializedBlocks);
  }

  get app() {
    return this.appService.app;
  }

  get isMobilePlatform() {
    return this.args.isMobilePlatform;
  }

  get allowedAttributes() {
    return this.attributeService.attributes.filter(
      (attr: IntercomAttribute) => attr.isUser && !attr.isUserEvent,
    );
  }

  get composerConfig() {
    let config = new ComposerConfig({
      resolver: new AttributeInfoResolver({
        attributes: this.attributeService.greetingComposerAttributes,
        attributeAllowlist: this.attributeService.attributesToGroupList(this.allowedAttributes),
        includeAppAttributes: false,
      }),
      placeholder: this.placeholder,
    });

    return config;
  }

  get placeholder() {
    return this.intl.t('messenger.greeting.greeting-placeholder');
  }

  get locale() {
    return this.args.locale;
  }

  get serializedBlocks() {
    return [
      {
        type: 'paragraph',
        text: (this.isMobilePlatform ? this.locale.mobileGreeting : this.locale.greeting) || '',
      },
    ];
  }

  @action updateBlocks(blocksDoc: BlocksDocument) {
    let blockFragments: Array<Block> = blocksDoc.blocks.map((block) =>
      createFragmentFromBlock(this.store, block),
    );

    let blockText = blockFragments.firstObject.text || '';
    let textContent = htmlToTextContent(blockText);
    blockText = textContent.trim();
    if (this.isMobilePlatform) {
      this.locale.mobileGreeting = blockText;
    } else {
      this.locale.greeting = blockText;
    }
  }

  @action onReady(composerApi: ComposerPublicAPI) {
    this.composerApi = composerApi;

    next(this, () => {
      this.composerApi?.composer.commands.focus();
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'MessengerSettings::Language::GreetingProsemirrorComposer': typeof GreetingProsemirrorComposer;
    'messenger-settings/language/greeting-prosemirror-composer': typeof GreetingProsemirrorComposer;
  }
}
